/**
 * Binaries API Bridge
 *
 * Client-side API for checking runtime binaries visible to Nest.
 */

import { isNestApiAvailable, requestNestMethod } from "../nest/client"
import type { HostBinary } from "../nest/generated"

// ============================================================================
// Type Definitions
// ============================================================================

export interface RuntimeBinaryStatus {
    name: string
    displayName: string
    version: string
    status: "available" | "not_found" | "error"
    path: string | null
    error: string | null
}

// ============================================================================
// API Functions
// ============================================================================

const DISPLAY_NAMES: Record<string, string> = {
    bun: "Bun",
    claude: "Claude Code",
    codex: "Codex",
    gh: "GitHub CLI",
    git: "Git",
    rg: "ripgrep",
}

function toStatus(binary: HostBinary): RuntimeBinaryStatus {
    return {
        name: binary.command,
        displayName: DISPLAY_NAMES[binary.command] ?? binary.command,
        version: binary.version ?? "unknown",
        status: binary.found ? "available" : "not_found",
        path: binary.path ?? null,
        error: binary.error ?? null,
    }
}

/** Get statuses of runtime binaries visible to Nest. */
export async function getStatuses(): Promise<RuntimeBinaryStatus[]> {
    try {
        if (!isNestApiAvailable()) throw new Error("Nest API is required for binary status checks")
        const result = await requestNestMethod("host.binary.list")
        return result.binaries.map(toStatus)
    } catch (error) {
        console.error("[BinariesAPI] Failed to get statuses:", error)
        return []
    }
}

/** Re-check whether a binary is available to Nest. */
export async function ensureBinary(name: string): Promise<{ ok: boolean; path?: string; error?: string }> {
    if (!isNestApiAvailable()) return { ok: false, error: "Nest API is required for binary status checks" }

    try {
        const result = await requestNestMethod("host.binary.check", { command: name })
        return result.found ? { ok: true, path: result.path } : { ok: false, error: result.error ?? `${name} was not found on PATH` }
    } catch (error) {
        console.error("[BinariesAPI] Failed to ensure binary:", error)
        return { ok: false, error: error instanceof Error ? error.message : "Unknown error" }
    }
}
